import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { canViewLiveAnalytics } from "@/lib/auth/orgSettings";
import { getOrgDomain } from "@/lib/powtoon-oauth";
import { fetchLiveAnalytics, type LiveSession } from "./live-client";
import { downloadLiveTranscript } from "./live-transcript";
import {
  AVATARS,
  SESSIONS,
  downloadTranscript as downloadDemoTranscript,
  type Avatar,
  type OriginKey,
  type Session,
} from "./demo-data";


export type RangeKey = "7d" | "30d" | "90d" | "all";

export const RANGES: { key: RangeKey; label: string; scale: number }[] = [
  { key: "7d", label: "Last 7 days", scale: 0.25 },
  { key: "30d", label: "Last 30 days", scale: 1 },
  { key: "90d", label: "Last 90 days", scale: 2.9 },
  { key: "all", label: "All time", scale: 4.4 },
];

/** Deterministic per-range drift applied to rate metrics (percentage points). */
const RATE_DRIFT: Record<RangeKey, number> = { "7d": 2, "30d": 0, "90d": -1, all: -3 };

const clampPct = (n: number) => Math.max(1, Math.min(99, Math.round(n)));
const scaleCount = (n: number, f: number) => Math.max(n > 0 ? 1 : 0, Math.round(n * f));

function scaleAvatar(a: Avatar, range: RangeKey): Avatar {
  const f = RANGES.find((r) => r.key === range)!.scale;
  const d = RATE_DRIFT[range];
  const origin = Object.fromEntries(
    (Object.keys(a.origin) as OriginKey[]).map((k) => [k, scaleCount(a.origin[k], f)]),
  ) as Record<OriginKey, number>;
  return {
    ...a,
    sessions: scaleCount(a.sessions, f),
    uniqueLearners: scaleCount(a.uniqueLearners, f),
    streamingTotal: scaleCount(a.streamingTotal, f),
    completionRate: clampPct(a.completionRate + d),
    passRate: a.passRate === null ? null : clampPct(a.passRate + d),
    avgScore: a.avgScore === null ? null : clampPct(a.avgScore + d / 2),
    origin,
    scoreDist:
      a.scoreDist?.map((b) => ({ ...b, n: scaleCount(b.n, f) })) ?? null,
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** Demo "today" — the baseline dataset ends here. */
const ANCHOR = new Date(Date.UTC(2026, 6, 12));

const label = (d: Date) => `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
const shiftDays = (days: number) => {
  const d = new Date(ANCHOR);
  d.setUTCDate(d.getUTCDate() - days);
  return d;
};

function bucketLabels(range: RangeKey): string[] {
  switch (range) {
    case "7d":
      return Array.from({ length: 7 }, (_, i) => label(shiftDays(6 - i)));
    case "30d":
      return Array.from({ length: 5 }, (_, i) => label(shiftDays((4 - i) * 7)));
    case "90d":
      return Array.from({ length: 13 }, (_, i) => label(shiftDays((12 - i) * 7)));
    case "all":
    default:
      return Array.from({ length: 12 }, (_, i) => label(shiftDays((11 - i) * 30)));
  }
}

/** Rising distribution of a total across n buckets — keeps the growth story. */
function distribute(total: number, n: number): number[] {
  const weights = Array.from({ length: n }, (_, i) => 0.75 + (n === 1 ? 0 : (i / (n - 1)) * 0.6));
  const sum = weights.reduce((s, w) => s + w, 0);
  return weights.map((w) => Math.max(1, Math.round((total * w) / sum)));
}

function scaleSessions(range: RangeKey): Session[] {
  const counts: Record<RangeKey, number> = { "7d": 4, "30d": 9, "90d": 18, all: 27 };
  const want = counts[range];
  const out: Session[] = [];
  for (let i = 0; i < want; i++) {
    const base = SESSIONS[i % SESSIONS.length]!;
    const cycle = Math.floor(i / SESSIONS.length);
    if (cycle === 0) {
      out.push(base);
      continue;
    }
    const shifted = shiftDays(cycle * 9 + (i % SESSIONS.length));
    out.push({
      ...base,
      id: `${base.id}-${cycle}`,
      when: `${label(shifted)}, ${base.when.split(", ")[1]}`,
      ts: base.ts - cycle * 1000,
    });
  }
  return out;
}

export type AnalyticsSource = "demo" | "live";

export type AnalyticsDataset = {
  range: RangeKey;
  rangeLabel: string;
  setRange: (r: RangeKey) => void;
  /** Which dataset is being displayed. */
  source: AnalyticsSource;
  setSource: (s: AnalyticsSource) => void;
  /** Whether this org may see real session data at all. */
  canUseLive: boolean;
  /** True once the live scope is known to have at least one recorded session. */
  hasLiveData: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  avatars: Avatar[];
  sessions: Session[];
  avatarById: (id: string) => Avatar | undefined;
  weeks: string[];
  minutes: number[];
  learners: number[];
  /** Export a session transcript — demo text for demo rows, the stored artifact for live rows. */
  downloadTranscript: (session: Session) => void;
};

const Ctx = createContext<AnalyticsDataset | null>(null);

const EMPTY_SERIES = { weeks: [] as string[], minutes: [] as number[], learners: [] as number[] };

export function AnalyticsRangeProvider({ children }: { children: ReactNode }) {
  const [range, setRange] = useState<RangeKey>("30d");
  const canUseLive = canViewLiveAnalytics(getOrgDomain());
  const [source, setSourceState] = useState<AnalyticsSource>("demo");
  // Once the trainer picks a source explicitly we stop auto-switching.
  const pinnedRef = useRef(false);

  const setSource = useCallback((s: AnalyticsSource) => {
    pinnedRef.current = true;
    setSourceState(s);
  }, []);

  const live = useQuery({
    queryKey: ["analytics-live", range],
    queryFn: () => fetchLiveAnalytics(range),
    enabled: canUseLive,
    staleTime: 60_000,
  });

  // Open on Demo until there is real data in scope, then switch over once.
  useEffect(() => {
    if (!pinnedRef.current && live.data?.hasAny) setSourceState("live");
  }, [live.data?.hasAny]);

  const demo = useMemo(() => {
    const avatars = AVATARS.map((a) => scaleAvatar(a, range));
    const sessions = scaleSessions(range);
    const weeks = bucketLabels(range);
    const totalMin = avatars.reduce((s, a) => s + a.streamingTotal, 0);
    const totalLearners = avatars.reduce((s, a) => s + a.uniqueLearners, 0);
    return {
      avatars,
      sessions,
      weeks,
      minutes: distribute(totalMin, weeks.length),
      learners: distribute(Math.round(totalLearners * 0.85), weeks.length),
    };
  }, [range]);

  const value = useMemo<AnalyticsDataset>(() => {
    const isLive = source === "live" && canUseLive;
    const data = isLive
      ? {
          avatars: live.data?.avatars ?? [],
          sessions: (live.data?.sessions ?? []) as Session[],
          weeks: live.data?.weeks ?? EMPTY_SERIES.weeks,
          minutes: live.data?.minutes ?? EMPTY_SERIES.minutes,
          learners: live.data?.learners ?? EMPTY_SERIES.learners,
        }
      : demo;

    return {
      range,
      rangeLabel: RANGES.find((r) => r.key === range)!.label,
      setRange,
      source: isLive ? "live" : "demo",
      setSource,
      canUseLive,
      hasLiveData: !!live.data?.hasAny,
      isLoading: isLive && live.isPending,
      error: isLive && live.error ? ((live.error as Error).message ?? "Analytics unavailable") : null,
      refetch: () => void live.refetch(),
      ...data,
      avatarById: (id: string) => data.avatars.find((a: Avatar) => a.id === id),
      downloadTranscript: (session: Session) => {
        if (isLive) void downloadLiveTranscript(session as LiveSession);
        else downloadDemoTranscript(session.id);
      },

    };
  }, [
    canUseLive,
    demo,
    live.data,
    live.error,
    live.isPending,
    live.refetch,
    range,
    setSource,
    source,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAnalyticsData(): AnalyticsDataset {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAnalyticsData must be used inside AnalyticsRangeProvider");
  return ctx;
}
